import { useEffect, useState } from 'react';
import { API_ENDPOINTS } from 'pi-kiosk-shared';
import { shouldEmitLogRepeat } from 'pi-kiosk-shared/logging';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { probeCustomerKioskSseAccess } from '../../../shared/realtime/probeCustomerKioskSseAccess.js';
import {
  getHttpStormRetryAfterMs,
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from '../../../shared/http/httpStormDetectedLatch.js';
import { sessionLog } from '../logging.js';

const SSE_RECONNECT_DELAY_MS = 3_000;

/**
 * Checkout kiosk SSE — listener / fast terminal UX only.
 * G-IMP-02: isConnected true on onopen OR first onmessage (hysteresis).
 * G-AMB-01: never gate FIO poll interval on isConnected.
 * G3a: fail-closed when realtime_device_transport is denied or neither
 * surface_kiosk nor surface_customer is entitled (Assert OR; defense-in-depth;
 * no reconnect spam on 403). CUSTOMER_ONLY / payment-only bookstore keeps
 * surface_customer + realtime ON without surface_kiosk — client skip is for
 * true unentitled/drift tenants, not a payment-only carve-out. Payment status
 * poll remains the source of truth.
 * G9: pause EventSource while HTTP_STORM_DETECTED latch is armed (close + no
 * reconnect stampede; resume after remaining TTL / latch clear).
 */
/** True only for a positive finite sales-point id (rejects null/undefined/NaN). */
export function isUsableCheckoutKioskId(
  kioskId: number | null | undefined,
): kioskId is number {
  return typeof kioskId === 'number' && Number.isFinite(kioskId) && kioskId > 0;
}

export function useCheckoutKioskSSE(kioskId: number | null | undefined): {
  isConnected: boolean;
} {
  const { apiBaseUrl } = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // `undefined <= 0` is false in JS — without this, String(undefined) → `/events/undefined` (422).
    if (!isUsableCheckoutKioskId(kioskId)) {
      return;
    }

    const eventsPath = API_ENDPOINTS.EVENTS.replace(':salesPointId', String(kioskId));
    const baseUrl = apiBaseUrl.replace(/\/+$/, '');
    const sseUrl = `${baseUrl}/api/${encodeURIComponent(tenantCode)}${eventsPath}`;

    let cancelled = false;
    let entitlementDenied = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const clearReconnectTimer = (): void => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const closeEventSource = (): void => {
      if (eventSource !== null) {
        eventSource.close();
        eventSource = null;
      }
      if (!cancelled) {
        setIsConnected(false);
      }
    };

    /** G9 — close live stream + cancel reconnect while latched (no stampede). */
    const pauseForStorm = (): void => {
      clearReconnectTimer();
      closeEventSource();
    };

    const connect = (): void => {
      if (cancelled || entitlementDenied) {
        return;
      }
      if (isHttpStormLatched()) {
        scheduleConnectAfterStorm();
        return;
      }
      clearReconnectTimer();
      closeEventSource();
      eventSource = new EventSource(sseUrl);
      eventSource.onopen = (): void => {
        if (!cancelled && !isHttpStormLatched()) {
          setIsConnected(true);
        }
      };
      eventSource.onmessage = (event): void => {
        // Hysteresis: some browsers/proxies deliver traffic before a reliable onopen.
        if (!cancelled && !isHttpStormLatched()) {
          setIsConnected(true);
        }
        window.dispatchEvent(
          new CustomEvent('websocket-message', {
            detail: { data: event.data },
          }),
        );
      };
      eventSource.onerror = (): void => {
        setIsConnected(false);
        eventSource?.close();
        eventSource = null;
        if (cancelled || entitlementDenied) {
          return;
        }
        if (isHttpStormLatched()) {
          scheduleConnectAfterStorm();
          return;
        }
        void probeCustomerKioskSseAccess(sseUrl).then((access) => {
          if (cancelled || entitlementDenied) {
            return;
          }
          if (access === 'denied') {
            entitlementDenied = true;
            if (shouldEmitLogRepeat(`customer-checkout-sse-denied:${kioskId}`, 1)) {
              sessionLog.warn('Checkout SSE entitlement denied — skipping reconnect', {
                operation: 'entitlement_denied',
                kioskId,
              });
            }
            return;
          }
          if (isHttpStormLatched()) {
            scheduleConnectAfterStorm();
            return;
          }
          if (shouldEmitLogRepeat(`customer-checkout-sse:${kioskId}`, 5)) {
            sessionLog.warn('Checkout SSE reconnect', {
              operation: 'reconnect',
              kioskId,
            });
          }
          reconnectTimer = setTimeout(connect, SSE_RECONNECT_DELAY_MS);
        });
      };
    };

    /**
     * Resume after latch TTL (fair-queue twin: poll live latch — no notify-on-expire).
     * Extends wait if latch was re-armed while sleeping.
     */
    const scheduleConnectAfterStorm = (): void => {
      if (cancelled || entitlementDenied) {
        return;
      }
      clearReconnectTimer();
      const waitMs = Math.max(getHttpStormRetryAfterMs(), 1);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (cancelled || entitlementDenied) {
          return;
        }
        if (isHttpStormLatched()) {
          scheduleConnectAfterStorm();
          return;
        }
        connect();
      }, waitMs);
    };

    const tryStart = (): void => {
      if (cancelled || entitlementDenied) {
        return;
      }
      if (isHttpStormLatched()) {
        pauseForStorm();
        scheduleConnectAfterStorm();
        return;
      }
      connect();
    };

    // G9 — do not probe/fetch while latched (guard throw → 'unknown' → connect stampede).
    if (isHttpStormLatched()) {
      tryStart();
    } else {
      void probeCustomerKioskSseAccess(sseUrl).then((access) => {
        if (cancelled) {
          return;
        }
        if (access === 'denied') {
          entitlementDenied = true;
          if (shouldEmitLogRepeat(`customer-checkout-sse-denied:${kioskId}`, 1)) {
            sessionLog.warn('Checkout SSE entitlement denied — skipping connect', {
              operation: 'entitlement_denied',
              kioskId,
            });
          }
          return;
        }
        tryStart();
      });
    }

    const unsubscribeLatch = subscribeHttpStormLatch(() => {
      if (cancelled || entitlementDenied) {
        return;
      }
      if (isHttpStormLatched()) {
        pauseForStorm();
        scheduleConnectAfterStorm();
        return;
      }
      // Latch cleared (tests / explicit clear) — cancel TTL resume timer, one connect.
      if (eventSource === null) {
        clearReconnectTimer();
        connect();
      }
    });

    return (): void => {
      cancelled = true;
      unsubscribeLatch();
      setIsConnected(false);
      clearReconnectTimer();
      eventSource?.close();
      eventSource = null;
    };
  }, [apiBaseUrl, tenantCode, kioskId]);

  return { isConnected };
}
